import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { FiCreditCard, FiCopy, FiExternalLink, FiPackage, FiTruck, FiMapPin, FiFileText, FiRefreshCw, FiX } from "react-icons/fi";
import { AiOutlineShoppingCart } from "react-icons/ai";
import Nav from "../../components/home/Nav";
import AccountList from "../../components/home/AccountList";
import Spinner from "../../components/Spinner";
import { useGetOrderDetailsQuery, useSubmitReturnRequestMutation } from "../../store/services/userOrdersService";
import { toman, toPersianNumber } from "../../utils/persian";
import { productUrl } from "../../utils/productUrl";
import { orderRef } from "../../utils/orderUrl";
import { useRequestPaymentMutation } from "../../store/services/paymentService";
import { useFeedback } from "../../context/AppFeedbackContext";

const statusLabels = {
  pending: "در انتظار بررسی",
  processing: "در حال آماده‌سازی",
  shipped: "ارسال شده",
  delivered: "تحویل شده",
  cancelled: "لغو شده",
};

const paymentLabels = {
  unpaid: "پرداخت نشده",
  paid: "پرداخت شده",
  failed: "ناموفق",
  refunded: "بازگشت وجه",
};

const returnStatusLabels = {
  pending: "در انتظار بررسی",
  reviewing: "در حال بررسی",
  approved: "تأیید شده",
  rejected: "رد شده",
  cancelled: "لغو شده",
};

const defaultShipmentSteps = [
  { key: "registered", label: "ثبت سفارش" },
  { key: "paid", label: "پرداخت" },
  { key: "preparing", label: "آماده‌سازی" },
  { key: "post", label: "ارسال" },
  { key: "done", label: "تحویل" },
];

const returnShipmentSteps = [
  { key: "registered", label: "ثبت سفارش" },
  { key: "paid", label: "پرداخت" },
  { key: "preparing", label: "آماده‌سازی" },
  { key: "post", label: "ارسال" },
  { key: "return", label: "مرجوعی" },
];

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString("fa-IR");
  } catch {
    return "-";
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const productImage = (item) => {
  const image = item?.variant?.image || item?.product?.main_image || item?.product?.gallery?.[0]?.url || item?.product?.gallery?.[0]?.image;
  if (!image) return "";
  if (String(image).startsWith("http")) return image;
  return `/storage/${image}`;
};

const postTrackingUrl = (code) => {
  if (!code) return "https://tracking.post.ir/";
  return `https://tracking.post.ir/?id=${encodeURIComponent(code)}`;
};

const latestReturnRequest = (order) => {
  const requests = order?.return_requests || order?.returnRequests || [];
  return requests.length ? requests[0] : null;
};

const timelineSteps = (order) => {
  const request = latestReturnRequest(order);

  if (!request) {
    return defaultShipmentSteps;
  }

  const label =
    request.status === "approved"
      ? "مرجوعی تأیید شد"
      : request.status === "rejected"
        ? "مرجوعی رد شد"
        : "مرجوعی";

  return returnShipmentSteps.map((step) => (
    step.key === "return" ? { ...step, label, status: request.status } : step
  ));
};

const shipmentProgress = (order) => {
  const request = latestReturnRequest(order);

  if (request) {
    if (["pending", "reviewing", "approved", "rejected"].includes(request.status)) return 4;
  }

  if (!order || order.status === "cancelled") return 0;
  if (order.payment_status !== "paid") return 0;
  if (order.status === "delivered") return 4;
  if (order.status === "shipped") return 3;
  if (order.status === "processing") return 2;
  return 1;
};

const isStepCompleted = (order, index, step) => {
  const request = latestReturnRequest(order);

  if (step.key === "return") {
    return request?.status === "approved";
  }

  return index < shipmentProgress(order);
};

function AccountModal({ open, title, children, onClose }) {
  if (!open) return null;

  return (
    <div className="account-modal-backdrop" onClick={onClose}>
      <div className="account-modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="account-modal-head">
          <h3>{title}</h3>
          <button type="button" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        <div className="account-modal-body">{children}</div>
      </div>
    </div>
  );
}

function ShipmentTimeline({ order }) {
  const active = shipmentProgress(order);
  const steps = timelineSteps(order);

  return (
    <div className="account-status-timeline">
      {steps.map((step, index) => (
        <div
          className={`account-status-step ${index <= active ? "is-active" : ""} ${step.key === "return" ? `is-return is-${step.status || "pending"}` : ""}`}
          key={step.key}
        >
          <span>{isStepCompleted(order, index, step) ? "✓" : index + 1}</span>
          <strong>{step.label}</strong>
        </div>
      ))}
    </div>
  );
}

const UserOrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data, isLoading, isFetching, isError } = useGetOrderDetailsQuery(id);
  const [requestPayment, paymentResponse] = useRequestPaymentMutation();
  const [submitReturnRequest, returnResponse] = useSubmitReturnRequestMutation();
  const [activeModal, setActiveModal] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [returnDescription, setReturnDescription] = useState("");
  const [returnItems, setReturnItems] = useState({});
  const [returnImages, setReturnImages] = useState([]);
  const { notify } = useFeedback();

  const order = data?.order;
  const items = order?.items || [];
  const returnRequests = order?.return_requests || order?.returnRequests || [];

  useEffect(() => {
    if (isError) {
      navigate("/", { replace: true });
    }
  }, [isError, navigate]);

  const canPayOrder =
    order &&
    order.sales_channel !== "offline" &&
    order.status !== "cancelled" &&
    order.payment_status !== "paid" &&
    order.payment_status !== "refunded";

  const canRequestReturn =
    order &&
    order.sales_channel !== "offline" &&
    order.status !== "cancelled" &&
    order.payment_status === "paid" &&
    !returnRequests.some((request) => ["pending", "reviewing"].includes(request.status));

  const selectedReturnItems = useMemo(() => {
    return Object.entries(returnItems)
      .map(([itemId, selected]) => {
        const orderItem = items.find((item) => String(item.id) === String(itemId));
        return {
          itemId,
          quantity: selected ? Number(orderItem?.quantity || 0) : 0,
        };
      })
      .filter((item) => item.quantity > 0);
  }, [returnItems]);

  const toggleReturnItem = (itemId) => {
    setReturnItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };


  const resetReturnForm = () => {
    setReturnReason("");
    setReturnDescription("");
    setReturnItems({});
    setReturnImages([]);
    setActiveModal("");
  };

  const copyTrackingCode = async () => {
    if (!order?.postal_tracking_code) return;

    try {
      await navigator.clipboard.writeText(order.postal_tracking_code);
      notify("کد رهگیری کپی شد.", "success", "رهگیری سفارش");
    } catch {
      const input = document.createElement("textarea");
      input.value = order.postal_tracking_code;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      notify("کد رهگیری کپی شد.", "success", "رهگیری سفارش");
    }
  };

  const sendReturnRequest = async () => {
    if (!order || returnResponse.isLoading) return;

    if (!returnReason.trim()) {
      notify("دلیل مرجوعی را وارد کنید.", "warning", "مرجوعی");
      return;
    }

    if (!selectedReturnItems.length) {
      notify("حداقل یک محصول را برای مرجوعی انتخاب کنید.", "warning", "مرجوعی");
      return;
    }

    const formData = new FormData();

    formData.append("reason", returnReason.trim());
    formData.append("description", returnDescription.trim());

    selectedReturnItems.forEach((item, index) => {
      formData.append(`items[${index}][order_item_id]`, item.itemId);
      formData.append(`items[${index}][quantity]`, String(item.quantity));
    });

    Array.from(returnImages || []).slice(0, 5).forEach((file, index) => {
      formData.append(`images[${index}]`, file);
    });

    try {
      await submitReturnRequest({
        id: orderRef(order || id),
        formData,
      }).unwrap();

      notify("درخواست مرجوعی ثبت شد و توسط پشتیبانی بررسی می‌شود.", "success", "مرجوعی");
      resetReturnForm();
    } catch (err) {
      notify(err?.data?.message || "خطا در ثبت درخواست مرجوعی.", "warning", "مرجوعی");
    }
  };

  const startPayment = async () => {
    const ref = orderRef(order || id);
    if (!ref || paymentResponse.isLoading) return;

    try {
      const res = await requestPayment(ref).unwrap();

      if (res.payment_url) {
        window.location.href = res.payment_url;
        return;
      }

      notify("درگاه پرداخت آماده نشد. لطفاً دوباره تلاش کنید.", "warning", "پرداخت");
    } catch (err) {
      notify(err?.data?.message || "امکان پرداخت دوباره این سفارش وجود ندارد.", "warning", "پرداخت");
    }
  };

  return (
    <>
      <Nav />

      <main className="account-page">
        <div className="my-container">
          <div className="account-shell">
            <AccountList />

            <section className="account-content">
              <div className="account-hero account-hero-compact">
                <div>
                  <span className="account-kicker">جزئیات سفارش</span>
                  <h1>{order?.order_number || "سفارش"}</h1>
                  <p>وضعیت ارسال و رهگیری سفارش شما</p>
                </div>

                <button type="button" className="account-hero-action" onClick={() => navigate("/orders")}>
                  <MdOutlineKeyboardBackspace size={19} />
                  بازگشت
                </button>
              </div>

              {isLoading || isFetching ? (
                <section className="account-card">
                  <Spinner />
                </section>
              ) : isError ? (
                <section className="account-card">
                  <div className="account-alert">خطا در دریافت جزئیات سفارش.</div>
                </section>
              ) : order ? (
                <>
                  <section className="account-card account-order-overview-clean">
                    <div className="account-card-head">
                      <div>
                        <span>مسیر سفارش</span>
                        <h2>{statusLabels[order.status] || "وضعیت سفارش"}</h2>
                      </div>
                      <FiPackage size={25} />
                    </div>

                    <ShipmentTimeline order={order} />

                    <div className="account-detail-minimal-grid">
                      <div>
                        <span>تاریخ سفارش</span>
                        <strong>{formatDate(order.created_at)}</strong>
                      </div>

                      <div>
                        <span>مبلغ</span>
                        <strong>{toman(order.total || 0)}</strong>
                      </div>

                      <div>
                        <span>پرداخت</span>
                        <strong>{paymentLabels[order.payment_status] || order.payment_status}</strong>
                      </div>
                    </div>

                    {canPayOrder && (
                      <button type="button" className="account-detail-pay" onClick={startPayment} disabled={paymentResponse.isLoading}>
                        <FiCreditCard size={18} />
                        {paymentResponse.isLoading ? "در حال اتصال..." : "پرداخت سفارش"}
                      </button>
                    )}
                  </section>

                  <section className={`account-card account-tracking-minimal-card ${order.postal_tracking_code ? "has-code" : ""}`}>
                    <div className="account-card-head">
                      <div>
                        <span>رهگیری پستی</span>
                        <h2>کد رهگیری پست</h2>
                      </div>
                      <FiTruck size={25} />
                    </div>

                    {order.postal_tracking_code ? (
                      <div className="account-tracking-minimal-box">
                        <strong dir="ltr">{order.postal_tracking_code}</strong>
                        <div>
                          <button type="button" onClick={copyTrackingCode}>
                            <FiCopy size={16} />
                            کپی
                          </button>
                          <a href={postTrackingUrl(order.postal_tracking_code)} target="_blank" rel="noreferrer">
                            <FiExternalLink size={16} />
                            رهگیری
                          </a>
                        </div>
                        <small>تحویل به پست: {formatDateTime(order.postal_shipped_at)}</small>
                      </div>
                    ) : (
                      <p className="account-soft-note">بعد از تحویل سفارش به پست، کد رهگیری اینجا نمایش داده می‌شود.</p>
                    )}
                  </section>

                  <section className="account-detail-actions-grid">
                    <button type="button" onClick={() => setActiveModal("items")}>
                      <AiOutlineShoppingCart size={20} />
                      <span>محصولات</span>
                    </button>

                    <button type="button" onClick={() => setActiveModal("shipping")}>
                      <FiMapPin size={20} />
                      <span>گیرنده و آدرس</span>
                    </button>

                    <button type="button" onClick={() => setActiveModal("invoice")}>
                      <FiFileText size={20} />
                      <span>فاکتور</span>
                    </button>

                    <button type="button" onClick={() => setActiveModal("return")}>
                      <FiRefreshCw size={20} />
                      <span>مرجوعی</span>
                    </button>
                  </section>

                  <div className="account-bottom-actions">
                    <Link to="/orders">بازگشت به سفارش‌ها</Link>
                    <Link to="/products">ادامه خرید</Link>
                  </div>

                  <AccountModal open={activeModal === "items"} title="محصولات سفارش" onClose={() => setActiveModal("")}>
                    <div className="account-order-items account-order-items-rich">
                      {items.map((item) => (
                        <Link to={productUrl(item?.product || item?.product_id, item?.product_name)} className="account-order-item account-order-item-link" key={item.id}>
                          <div className="account-order-item-image">
                            {productImage(item) ? <img src={productImage(item)} alt={item.product_name || ""} /> : null}
                          </div>

                          <div className="account-order-item-title">
                            <strong>{item.product_name || item.product?.name || "-"}</strong>
                            <span>{[item.color, item.size].filter(Boolean).join(" / ") || "بدون تنوع"}</span>
                          </div>

                          <div className="account-order-item-price">
                            <span>{toPersianNumber(item.quantity || 0)} عدد</span>
                            <strong>{toman(item.total_price)}</strong>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </AccountModal>

                  <AccountModal open={activeModal === "shipping"} title="گیرنده و آدرس" onClose={() => setActiveModal("")}>
                    <div className="account-info-list">
                      <div>
                        <span>نام گیرنده</span>
                        <strong>{order.customer_name || "-"}</strong>
                      </div>

                      <div>
                        <span>موبایل</span>
                        <strong className="ltr">{order.customer_mobile || "-"}</strong>
                      </div>

                      <div>
                        <span>روش ارسال</span>
                        <strong>{order.shipping_method_title || order.shipping_method?.title || "-"}</strong>
                      </div>

                      <div className="account-info-wide">
                        <span>آدرس</span>
                        <strong>{order.address || "-"}</strong>
                      </div>
                    </div>
                  </AccountModal>

                  <AccountModal open={activeModal === "invoice"} title="فاکتور سفارش" onClose={() => setActiveModal("")}>
                    <div className="account-invoice-box">
                      <div>
                        <span>جمع محصولات</span>
                        <strong>{toman(order.subtotal || 0)}</strong>
                      </div>

                      <div>
                        <span>هزینه ارسال</span>
                        <strong>{order.shipping_cost ? toman(order.shipping_cost) : "پس‌کرایه / رایگان"}</strong>
                      </div>

                      <div>
                        <span>تخفیف</span>
                        <strong>{toman((order.discount_amount || 0) + (order.shipping_discount_amount || 0))}</strong>
                      </div>

                      <div className="account-invoice-total">
                        <span>مبلغ نهایی</span>
                        <strong>{toman(order.total || 0)}</strong>
                      </div>
                    </div>
                  </AccountModal>

                  <AccountModal open={activeModal === "return"} title="درخواست مرجوعی" onClose={() => setActiveModal("")}>
                    {returnRequests.length > 0 && (
                      <div className="account-return-requests account-return-requests-clean">
                        {returnRequests.map((request) => (
                          <article className={`account-return-request is-${request.status}`} key={request.id}>
                            <div>
                              <span>وضعیت</span>
                              <strong>{returnStatusLabels[request.status] || request.status}</strong>
                            </div>

                            <div>
                              <span>دلیل</span>
                              <strong>{request.reason || "-"}</strong>
                            </div>

                            {request.description && <p>{request.description}</p>}

                            {request.admin_response && (
                              <div className="account-return-admin-response">
                                <span>پاسخ پشتیبانی</span>
                                <strong>{request.admin_response}</strong>
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    )}

                    {canRequestReturn ? (
                      <div className="account-return-form account-return-form-modern">
                        <label>
                          دلیل مرجوعی
                          <input
                            type="text"
                            value={returnReason}
                            onChange={(event) => setReturnReason(event.target.value)}
                            placeholder="مثلاً مغایرت سایز یا ایراد ظاهری"
                          />
                        </label>

                        <label>
                          توضیحات بیشتر
                          <textarea
                            value={returnDescription}
                            onChange={(event) => setReturnDescription(event.target.value)}
                            placeholder="توضیحات کوتاه و واضح بنویسید."
                            rows={4}
                          />
                        </label>

                        <div className="account-return-item-picker">
                          {items.map((item) => (
                            <div className="account-return-item-row" key={item.id}>
                              <div>
                                <strong>{item.product_name || item.product?.name || "-"}</strong>
                                <span>{[item.color, item.size].filter(Boolean).join(" / ") || "بدون تنوع"}</span>
                              </div>

                              <button
                                type="button"
                                className={returnItems[item.id] ? "account-return-select is-selected" : "account-return-select"}
                                onClick={() => toggleReturnItem(item.id)}
                              >
                                {returnItems[item.id] ? "انتخاب شد" : "انتخاب"}
                              </button>
                            </div>
                          ))}
                        </div>

                        <label>
                          عکس محصول یا مشکل
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            onChange={(event) => setReturnImages(event.target.files)}
                          />
                          <small>حداکثر ۵ عکس واضح ارسال کنید.</small>
                        </label>

                        <div className="account-return-form-actions">
                          <button type="button" onClick={sendReturnRequest} disabled={returnResponse.isLoading}>
                            {returnResponse.isLoading ? "در حال ثبت..." : "ارسال درخواست"}
                          </button>

                          <button type="button" className="secondary" onClick={resetReturnForm} disabled={returnResponse.isLoading}>
                            انصراف
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="account-return-note">
                        {returnRequests.some((request) => ["pending", "reviewing"].includes(request.status))
                          ? "درخواست مرجوعی شما در حال بررسی است."
                          : "برای این سفارش فعلاً امکان ثبت درخواست مرجوعی وجود ندارد."}
                      </div>
                    )}
                  </AccountModal>
                </>
              ) : (
                <section className="account-card">
                  <div className="account-alert">سفارش پیدا نشد.</div>
                </section>
              )}
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserOrderDetails;
